import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowsDownUp, ChartLineUp, ClockCounterClockwise, CirclesThreePlus, TrendUp } from 'phosphor-react';
import { OptionsOnboardingCard } from '../../components/options/OptionsOnboardingCard';
import { OptionsPageShell } from '../../components/options/OptionsPageShell';
import OptionsLoadingState from '../../components/options/OptionsLoadingState';
import { OptionsAnalyticsControls } from '../../components/options/OptionsAnalyticsControls';
import { OptionsAnalyticsCyclesSection } from '../../components/options/OptionsAnalyticsCyclesSection';
import { OptionsAnalyticsModuleCard } from '../../components/options/OptionsAnalyticsModuleCard';
import { OptionsAnalyticsModuleOrderEditor } from '../../components/options/OptionsAnalyticsModuleOrderEditor';
import { OptionsAnalyticsOverview } from '../../components/options/OptionsAnalyticsOverview';
import { OptionsAnalyticsEntryVsExitDteSection, OptionsAnalyticsPerformanceQualitySection, OptionsAnalyticsTradeResultsSection } from '../../components/options/OptionsAnalyticsPerformanceQualitySection';
import { OptionsAnalyticsSectionTable } from '../../components/options/OptionsAnalyticsSectionTable';
import { OptionsPortfolioRisk } from '../../components/options/OptionsPortfolioRisk';
import { Button } from '@/components/ui/button';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import useOptionsAnalyticsData from '../../hooks/useOptionsAnalyticsData';
import { useOptionsOverviewState } from '../../hooks/useOptionsOverviewState';
import { useOptionsPortfolioRiskData } from '../../hooks/useOptionsPortfolioRiskData';
import { formatAnalyticsDays } from '../../components/options/optionsAnalyticsUtils';

const ANALYTICS_MODULE_ORDER_STORAGE_KEY = 'geni_options_analytics_module_order_v2';
const DEFAULT_MODULE_ORDER = [
  'performance-by-asset',
  'performance-by-strategy',
  'position-cycles',
  'cycles-by-roll-count',
  'performance-by-entry-dte',
  'performance-by-entry-delta',
  'performance-by-duration',
  'entry-vs-exit-dte',
  'trade-results',
];

const MODULE_GROUP_MAP = {
  'performance-by-asset': 'performance',
  'performance-by-strategy': 'performance',
  'position-cycles': 'positionManagement',
  'cycles-by-roll-count': 'positionManagement',
  'performance-by-entry-dte': 'timing',
  'performance-by-entry-delta': 'timing',
  'performance-by-duration': 'timing',
  'entry-vs-exit-dte': 'timing',
  'trade-results': 'results',
};

const mergeModuleOrder = (order = []) => {
  const sanitized = order.filter((item) => DEFAULT_MODULE_ORDER.includes(item));
  return [...sanitized, ...DEFAULT_MODULE_ORDER.filter((item) => !sanitized.includes(item))];
};

const moveItem = (items, fromIndex, toIndex) => {
  const next = [...items];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
};

export default function OptionsAnalytics() {
  const { t, formatMessage } = useOptionsLocale();
  const { connected, loading: overviewLoading } = useOptionsOverviewState();
  const { accountData, loading: portfolioRiskLoading } = useOptionsPortfolioRiskData(connected);
  const {
    analyticsData,
    loading: analyticsLoading,
    selectedCurrency,
    setSelectedCurrency,
    selectedRange,
    setSelectedRange,
    customRange,
    setCustomRange,
    underlyingFilter,
    setUnderlyingFilter,
    strategyFilter,
    setStrategyFilter,
    strategyOptions,
    handleApplyCustomRange,
  } = useOptionsAnalyticsData(connected);
  const [expandedModuleId, setExpandedModuleId] = useState(null);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [moduleOrder, setModuleOrder] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_MODULE_ORDER;
    try {
      const raw = window.localStorage.getItem(ANALYTICS_MODULE_ORDER_STORAGE_KEY);
      if (!raw) return DEFAULT_MODULE_ORDER;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? mergeModuleOrder(parsed) : DEFAULT_MODULE_ORDER;
    } catch {
      return DEFAULT_MODULE_ORDER;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(ANALYTICS_MODULE_ORDER_STORAGE_KEY, JSON.stringify(moduleOrder));
  }, [moduleOrder]);

  const listSummary = useCallback((labels = [], fallback) => {
    if (!labels.length) return fallback;
    if (labels.length <= 3) return labels.join(' · ');
    return formatMessage(t('analytics.moduleSummary.assetCount'), { count: labels.length });
  }, [formatMessage, t]);

  const formatCountSummary = useCallback((count) => formatMessage(t('analytics.moduleSummary.bucketCount'), { count }), [formatMessage, t]);

  const translatedCycleRollBucketRows = useMemo(() => {
    const rows = analyticsData?.cycle_summary?.roll_bucket_rows || [];
    return rows.map((row) => ({
      ...row,
      label: row.key === 'rolls_0'
        ? t('analytics.rollBucket0')
        : row.key === 'rolls_1'
          ? t('analytics.rollBucket1')
          : row.key === 'rolls_2'
            ? t('analytics.rollBucket2')
            : row.key === 'rolls_3_plus'
              ? t('analytics.rollBucket3Plus')
              : row.label,
    }));
  }, [analyticsData, t]);

  const moduleDefinitions = useMemo(() => {
    const qualityMetrics = analyticsData?.performance_quality?.metrics || {};
    const underlyingRows = analyticsData?.underlying_rows || [];
    const visibleUnderlyingRows = underlyingRows.filter((row) => Number(row?.metrics?.operations_count || 0) > 0);
    const strategyRows = (analyticsData?.strategy_rows || []).filter((row) => Number(row?.metrics?.operations_count || 0) > 0);
    const entryDteRows = (analyticsData?.entry_dte_rows || []).filter((row) => Number(row?.metrics?.operations_count || 0) > 0);
    const entryDeltaRows = (analyticsData?.entry_delta_rows || []).filter((row) => Number(row?.metrics?.operations_count || 0) > 0);
    const durationRows = (analyticsData?.duration_rows || []).filter((row) => Number(row?.metrics?.operations_count || 0) > 0);
    const cycleRollRows = translatedCycleRollBucketRows.filter((row) => Number(row?.metrics?.operations_count || 0) > 0);
    const hasCycleData = Number(analyticsData?.cycle_summary?.total_cycles || 0) > 0 || cycleRollRows.length > 0 || (analyticsData?.cycle_timelines || []).length > 0;
    const hasTradeResultsData = Number(qualityMetrics?.operations_count || 0) > 0;
    const hasEntryExitDteData = [qualityMetrics?.average_entry_dte, qualityMetrics?.average_exit_dte, qualityMetrics?.average_dte_reduction].some((value) => value !== null && value !== undefined);

    return {
      'performance-by-asset': {
        id: 'performance-by-asset',
        title: t('analytics.performanceByUnderlying'),
        description: t('analytics.moduleCards.performanceByAssetDescription'),
        summary: listSummary(visibleUnderlyingRows.map((row) => row.label), t('analytics.notEnoughDataForAnalysis')),
        hasData: visibleUnderlyingRows.length > 0,
        icon: ChartLineUp,
        content: <OptionsAnalyticsSectionTable title={t('analytics.performanceByUnderlying')} rows={analyticsData?.underlying_rows || []} selectedCurrency={selectedCurrency} testIdPrefix="options-analytics-underlying" showAssetIcon showFees />,
      },
      'performance-by-strategy': {
        id: 'performance-by-strategy',
        title: t('analytics.performanceByStrategy'),
        description: t('analytics.moduleCards.performanceByStrategyDescription'),
        summary: strategyRows.length
          ? formatMessage(t('analytics.moduleSummary.bucketCount'), { count: strategyRows.length })
          : t('analytics.notEnoughDataForAnalysis'),
        hasData: strategyRows.length > 0,
        icon: TrendUp,
        content: <OptionsAnalyticsSectionTable title={t('analytics.performanceByStrategy')} rows={analyticsData?.strategy_rows || []} selectedCurrency={selectedCurrency} testIdPrefix="options-analytics-strategy" showFees />,
      },
      'position-cycles': {
        id: 'position-cycles',
        title: t('analytics.moduleCards.positionCyclesTitle'),
        description: t('analytics.moduleCards.positionCyclesDescription'),
        summary: hasCycleData
          ? formatMessage(t('analytics.moduleSummary.cyclesMeta'), { cycles: analyticsData?.cycle_summary?.total_cycles || 0, rolls: analyticsData?.cycle_summary?.total_rolls || 0 })
          : t('analytics.notEnoughDataForAnalysis'),
        hasData: hasCycleData,
        icon: CirclesThreePlus,
        content: <OptionsAnalyticsCyclesSection summary={analyticsData?.cycle_summary} timelines={analyticsData?.cycle_timelines || []} selectedCurrency={selectedCurrency} showRollBucketTable={false} />,
      },
      'cycles-by-roll-count': {
        id: 'cycles-by-roll-count',
        title: t('analytics.cyclesByRollCount'),
        description: t('analytics.moduleCards.cyclesByRollCountDescription'),
        summary: cycleRollRows.length
          ? formatCountSummary(cycleRollRows.length)
          : t('analytics.notEnoughDataForAnalysis'),
        hasData: cycleRollRows.length > 0,
        icon: CirclesThreePlus,
        content: (
          <OptionsAnalyticsSectionTable
            title={t('analytics.cyclesByRollCount')}
            helper={t('analytics.cyclesByRollCountHelper')}
            rows={translatedCycleRollBucketRows}
            selectedCurrency={selectedCurrency}
            testIdPrefix="options-analytics-cycle-buckets"
            showFees={false}
            operationsLabel={t('analytics.cyclesCountShort')}
          />
        ),
      },
      'performance-by-entry-dte': {
        id: 'performance-by-entry-dte',
        title: t('analytics.performanceByEntryDte'),
        description: t('analytics.moduleCards.performanceByEntryDteDescription'),
        summary: entryDteRows.length
          ? formatMessage(t('analytics.moduleSummary.bucketCount'), { count: entryDteRows.length })
          : t('analytics.notEnoughDataForAnalysis'),
        hasData: entryDteRows.length > 0,
        icon: ClockCounterClockwise,
        content: <OptionsAnalyticsSectionTable title={t('analytics.performanceByEntryDte')} rows={analyticsData?.entry_dte_rows || []} selectedCurrency={selectedCurrency} testIdPrefix="options-analytics-entry-dte" showFees={false} />,
      },
      'performance-by-entry-delta': {
        id: 'performance-by-entry-delta',
        title: t('analytics.performanceByEntryDelta'),
        description: t('analytics.moduleCards.performanceByDeltaDescription'),
        summary: entryDeltaRows.length
          ? formatMessage(t('analytics.moduleSummary.bucketCount'), { count: entryDeltaRows.length })
          : t('analytics.notEnoughDataForAnalysis'),
        hasData: entryDeltaRows.length > 0,
        icon: ChartLineUp,
        content: <OptionsAnalyticsSectionTable title={t('analytics.performanceByEntryDelta')} rows={analyticsData?.entry_delta_rows || []} selectedCurrency={selectedCurrency} testIdPrefix="options-analytics-entry-delta" showFees={false} helper={t('analytics.entryDeltaCoverage')} />,
      },
      'performance-by-duration': {
        id: 'performance-by-duration',
        title: t('analytics.performanceByHoldTime'),
        description: t('analytics.moduleCards.performanceByDurationDescription'),
        summary: durationRows.length
          ? formatMessage(t('analytics.moduleSummary.bucketCount'), { count: durationRows.length })
          : t('analytics.notEnoughDataForAnalysis'),
        hasData: durationRows.length > 0,
        icon: ClockCounterClockwise,
        content: <OptionsAnalyticsSectionTable title={t('analytics.performanceByHoldTime')} rows={analyticsData?.duration_rows || []} selectedCurrency={selectedCurrency} testIdPrefix="options-analytics-duration" showFees={false} />,
      },
      'entry-vs-exit-dte': {
        id: 'entry-vs-exit-dte',
        title: t('analytics.entryVsExitDte'),
        description: t('analytics.moduleCards.entryVsExitDteDescription'),
        summary: hasEntryExitDteData
          ? formatMessage(t('analytics.moduleSummary.entryVsExitDteMeta'), { value: formatAnalyticsDays(qualityMetrics?.average_dte_reduction) })
          : t('analytics.notEnoughDataForAnalysis'),
        hasData: hasEntryExitDteData,
        icon: ClockCounterClockwise,
        content: <OptionsAnalyticsEntryVsExitDteSection performanceQuality={analyticsData?.performance_quality} />,
      },
      'trade-results': {
        id: 'trade-results',
        title: t('analytics.moduleCards.tradeResultsTitle'),
        description: t('analytics.moduleCards.tradeResultsDescription'),
        summary: hasTradeResultsData
          ? formatMessage(t('analytics.moduleSummary.tradeResultsMeta'), {
            winStreak: String(analyticsData?.performance_quality?.longest_win_streak || 0),
            lossStreak: String(analyticsData?.performance_quality?.longest_loss_streak || 0),
          })
          : t('analytics.notEnoughDataForAnalysis'),
        hasData: hasTradeResultsData,
        icon: TrendUp,
        content: <OptionsAnalyticsTradeResultsSection performanceQuality={analyticsData?.performance_quality} selectedCurrency={selectedCurrency} />,
      },
    };
  }, [
    analyticsData,
    formatCountSummary,
    formatMessage,
    listSummary,
    selectedCurrency,
    t,
    translatedCycleRollBucketRows,
  ]);

  const orderedModules = useMemo(
    () => mergeModuleOrder(moduleOrder).map((id) => moduleDefinitions[id]).filter(Boolean),
    [moduleDefinitions, moduleOrder],
  );

  const moduleGroupBreaks = useMemo(() => {
    const breaks = {};
    let lastGroup = null;
    orderedModules.forEach((module) => {
      const group = MODULE_GROUP_MAP[module.id];
      if (group && group !== lastGroup) {
        breaks[module.id] = group;
      }
      lastGroup = group;
    });
    return breaks;
  }, [orderedModules]);

  const isModuleExpanded = useCallback((moduleId) => expandedModuleId === moduleId, [expandedModuleId]);

  const handleToggleModule = useCallback((moduleId) => {
    setExpandedModuleId((current) => (current === moduleId ? null : moduleId));
  }, []);

  const handleMoveModule = (moduleId, direction) => {
    setModuleOrder((current) => {
      const safe = mergeModuleOrder(current);
      const fromIndex = safe.indexOf(moduleId);
      const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
      if (fromIndex < 0 || toIndex < 0 || toIndex >= safe.length) return safe;
      return moveItem(safe, fromIndex, toIndex);
    });
  };

  return (
    <OptionsPageShell
      title={t('analytics.title')}
      description={t('analytics.description')}
      eyebrow={t('analytics.eyebrow')}
      hideHeader
    >
      {overviewLoading ? (
        <OptionsLoadingState
          title={t('common.connectDeribit')}
          description={t('analytics.description')}
          testId="options-analytics-loading-overview"
        />
      ) : !connected ? (
        <OptionsOnboardingCard
          title={t('onboarding.title')}
          description={t('onboarding.description')}
          connectLabel={t('common.connectDeribit')}
          securityTitle={t('onboarding.securityTitle')}
          securityBody={t('onboarding.securityBody')}
          securityDetails={t('onboarding.securityDetails')}
        />
      ) : (
        <div className="space-y-6" data-testid="options-analytics-root">
          <OptionsAnalyticsControls
            selectedCurrency={selectedCurrency}
            onCurrencyChange={setSelectedCurrency}
            selectedRange={selectedRange}
            onRangeChange={setSelectedRange}
            customRange={customRange}
            onCustomRangeChange={setCustomRange}
            onApplyCustomRange={handleApplyCustomRange}
            underlyingFilter={underlyingFilter}
            onUnderlyingChange={setUnderlyingFilter}
            strategyFilter={strategyFilter}
            onStrategyChange={setStrategyFilter}
            strategyOptions={strategyOptions}
          />

          {analyticsLoading ? (
            <OptionsLoadingState
              title={t('common.syncingNow')}
              description={t('analytics.description')}
              minHeight={240}
              testId="options-analytics-loading-data"
            />
          ) : (
            <>
              <OptionsAnalyticsOverview overview={analyticsData?.overview} performanceQuality={analyticsData?.performance_quality} selectedCurrency={selectedCurrency} />
              <OptionsAnalyticsPerformanceQualitySection performanceQuality={analyticsData?.performance_quality} selectedCurrency={selectedCurrency} />
              <OptionsPortfolioRisk accountData={accountData} loading={portfolioRiskLoading} underlyingFilter={underlyingFilter} />

              <section className="space-y-3" data-testid="options-analytics-detailed-modules-section">
                <div className="flex flex-wrap items-center justify-between gap-3" data-testid="options-analytics-detailed-modules-header">
                  <div className="max-w-3xl">
                    <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/45" data-testid="options-analytics-detailed-modules-title">{t('analytics.detailedAnalyses')}</p>
                    <p className="mt-1 text-sm text-white/52" data-testid="options-analytics-detailed-modules-helper">{t('analytics.detailedAnalysesHelper')}</p>
                  </div>

                  <Button variant="outline" size="sm" onClick={() => setIsEditingOrder((current) => !current)} data-testid="options-analytics-edit-order-button">
                    <ArrowsDownUp size={14} className="mr-2" />
                    {t('analytics.editOrder')}
                  </Button>
                </div>

                {isEditingOrder ? (
                  <OptionsAnalyticsModuleOrderEditor
                    modules={orderedModules}
                    onMoveUp={(moduleId) => handleMoveModule(moduleId, 'up')}
                    onMoveDown={(moduleId) => handleMoveModule(moduleId, 'down')}
                    onReset={() => setModuleOrder(DEFAULT_MODULE_ORDER)}
                    t={t}
                  />
                ) : null}

                <div className="grid gap-2 md:grid-cols-2" data-testid="options-analytics-module-cards-grid">
                  {orderedModules.map((module, index) => {
                    const groupLabel = moduleGroupBreaks[module.id];
                    return (
                      <React.Fragment key={module.id}>
                        {groupLabel ? (
                          <div className={`md:col-span-2 ${index === 0 ? '' : 'pt-2'}`} data-testid={`options-analytics-group-${groupLabel}`}>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/25">
                              {t(`analytics.groups.${groupLabel}`)}
                            </p>
                          </div>
                        ) : null}
                        <div className="min-w-0" data-testid={`options-analytics-module-slot-${module.id}`}>
                          <OptionsAnalyticsModuleCard module={module} expanded={isModuleExpanded(module.id)} onToggle={handleToggleModule} />
                        </div>
                        {isModuleExpanded(module.id) ? (
                          <div className="min-w-0 md:col-span-2" data-testid={`options-analytics-module-expanded-${module.id}`}>
                            <div className="overflow-hidden rounded-2xl border border-white/12 bg-[linear-gradient(180deg,rgba(255,255,255,0.045),rgba(255,255,255,0.02))] p-4 sm:p-5">
                              {module.hasData ? module.content : (
                                <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 px-4 py-5 text-sm text-white/55" data-testid={`options-analytics-module-expanded-${module.id}-empty-state`}>
                                  {t('analytics.notEnoughDataForAnalysis')}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>
      )}
    </OptionsPageShell>
  );
}
